/**
 * Сертификат камня: снять, прочитать, проверить.
 *
 * Разделение на два шага здесь не бюрократия. Фотография отчёта доказывает,
 * что бумага существует, но не что она подлинная — подделка отчёта реальный
 * риск в этой торговле. Поэтому прочитанное живёт как «заявлено», а
 * «сертифицировано» появляется только после проверки у лаборатории.
 */
import { useEffect, useRef, useState } from 'react';
import { db, enqueue, type LocalStone } from '../db';
import { apiFetch, syncNow } from '../sync';
import { grabFrame, openCamera } from '../lib/media';
import { ulid } from '@core';

type Phase = 'camera' | 'reading' | 'review' | 'done';

/** Снятая страница отчёта. Их бывает несколько, и читать их надо вместе. */
interface Page { full: Blob; preview: Blob; url: string }

interface Cert {
  lab?: string; lab_name?: string;
  report_no?: string; report_date?: string;
  stone_type?: string; species?: string; variety?: string;
  weight_ct?: string; measurements?: string; shape?: string; cut?: string;
  color?: string; origin?: string; treatment?: string; comments?: string;
  has_qr?: boolean;
  uncertain?: string[];
}

interface Dup {
  certId: string; stoneId: string; lab?: string | null;
  no?: string | null; supplier?: string | null; at?: string;
}

interface Lab {
  code: string; name: string; verify_method: string;
  verify_url: string | null; required_fields: string[];
}

const HOW_TO_VERIFY: Record<string, string> = {
  api: 'Проверяется автоматически по номеру отчёта.',
  qr: 'На отчёте есть QR-код — отсканируйте его камерой телефона, откроется страница лаборатории.',
  web_form: 'Откройте страницу лаборатории и введите номер отчёта.',
  contact: 'Онлайн-проверки у этой лаборатории нет. Подтверждение запрашивают письмом.',
  none: 'Способ проверки не известен.',
};

const FIELDS: Array<[keyof Cert, string]> = [
  ['stone_type', 'Камень'],
  ['variety', 'Разновидность'],
  ['weight_ct', 'Вес, ct'],
  ['shape', 'Форма'],
  ['cut', 'Огранка'],
  ['color', 'Цвет'],
  ['origin', 'Происхождение'],
  ['treatment', 'Обработка'],
  ['measurements', 'Размеры'],
  ['comments', 'Примечания'],
];

export default function CertScan({ stone, onDone, onCancel }: {
  stone: LocalStone;
  onDone: () => void;
  onCancel: () => void;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);

  const [phase, setPhase] = useState<Phase>('camera');
  const [error, setError] = useState('');
  const [pages, setPages] = useState<Page[]>([]);
  const [cert, setCert] = useState<Cert>({});
  const [lab, setLab] = useState<Lab | null>(null);
  const [dup, setDup] = useState<Dup | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (phase !== 'camera') return;
    let dead = false;
    (async () => {
      try {
        const stream = await openCamera();
        if (dead) { stream.getTracks().forEach((t) => t.stop()); return; }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play().catch(() => {});
        }
      } catch (e: any) {
        setError(e?.name === 'NotAllowedError'
          ? 'Камера запрещена. Разрешите доступ в настройках сайта.'
          : `Камера недоступна: ${e?.message ?? e}`);
      }
    })();
    return () => {
      dead = true;
      streamRef.current?.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    };
  }, [phase]);

  useEffect(() => () => { pages.forEach((p) => URL.revokeObjectURL(p.url)); },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []);

  /** Снять очередную страницу. Разбор не начинается, пока человек не сказал «готово». */
  async function shoot() {
    if (!videoRef.current) return;
    try {
      const frames = await grabFrame(videoRef.current);
      setPages((p) => [...p, { ...frames, url: URL.createObjectURL(frames.preview) }]);
      setError('');
    } catch (e: any) {
      setError(`Не получилось снять: ${e?.message ?? e}`);
    }
  }

  /** Прочитать всё снятое разом: номер на одной странице, происхождение на другой. */
  async function read() {
    if (!pages.length) return;
    streamRef.current?.getTracks().forEach((t) => t.stop());
    setPhase('reading');

    if (!navigator.onLine) {
      setError('Без сети отчёт не прочитать. Снимки сохранятся — сервер разберёт их, когда появится связь.');
      setPhase('review');
      return;
    }
    try {
      const fd = new FormData();
      pages.forEach((p, i) => fd.append(`page${i}`, p.preview, `cert${i}.webp`));
      const res = await apiFetch('/api/scan-certificate', { method: 'POST', body: fd });
      if (!res.ok) throw new Error(`сервер ответил ${res.status}`);
      const d = (await res.json()) as { cert: Cert; lab: Lab | null; duplicate: Dup | null };
      setCert(d.cert ?? {});
      setLab(d.lab);
      setDup(d.duplicate ?? null);
      setPhase('review');
    } catch (e: any) {
      setError(`Не удалось прочитать: ${e?.message ?? e}. Снимки всё равно сохранятся.`);
      setPhase('review');
    }
  }

  async function save() {
    setSaving(true);
    const now = new Date().toISOString();
    const id = ulid();

    // Все страницы ложатся к камню; первая идёт как основная — по ней
    // сервер потом дочитает отчёт, если сейчас не было связи.
    const mediaIds = pages.map(() => ulid());
    for (let i = 0; i < pages.length; i++) {
      await db.media.put({
        id: mediaIds[i], subjectId: stone.id, role: 'certificate', blob: pages[i].full,
        mime: pages[i].full.type, bytes: pages[i].full.size, uploaded: false, createdAt: now,
      });
    }

    const read = Object.keys(cert).length > 0;
    await db.certs.put({
      id, stoneId: stone.id,
      labCode: cert.lab ? cert.lab.toUpperCase() : null,
      labName: lab?.name ?? cert.lab_name ?? null,
      reportNo: cert.report_no ?? null,
      reportDate: cert.report_date ?? null,
      raw: read ? (cert as Record<string, unknown>) : null,
      uncertain: cert.uncertain ?? [],
      verifyMethod: lab?.verify_method ?? null,
      verifyUrl: lab?.verify_url ?? null,
      scanStatus: read ? 'done' : 'pending',
      createdAt: now,
    });
    await enqueue('cert.add',
      { id, stoneId: stone.id, mediaId: mediaIds[0], cert: read ? cert : {} }, ulid());

    void syncNow();
    setSaving(false);
    setPhase('done');
  }

  // ── камера ────────────────────────────────────────────────────────────────
  if (phase === 'camera') {
    return (
      <div className="cam">
        <div className="lens">
          <video ref={videoRef} playsInline muted autoPlay />
          <div className="lenstop">
            <span className="glass" style={{ flex: 1 }}>
              {pages.length ? `Снято страниц: ${pages.length}` : 'Отчёт целиком в кадр'}
            </span>
            <button className="glass round" onClick={onCancel} aria-label="Закрыть">✕</button>
          </div>
          <div className="guides"><i /><i /><i /><i /></div>

          {/* Снятые страницы видно всё время: сколько их и что на них попало. */}
          {pages.length > 0 && (
            <div className="pagestrip">
              {pages.map((p, i) => (
                <span key={p.url} className="pagethumb">
                  <img src={p.url} alt={`страница ${i + 1}`} />
                  <b>{i + 1}</b>
                </span>
              ))}
            </div>
          )}

          {error && <div className="readout"><div className="panel">{error}</div></div>}
        </div>

        <div className="deck">
          <span style={{ width: 62 }} />
          <button className="trigger" onClick={() => void shoot()} disabled={Boolean(error)}
                  aria-label={pages.length ? 'Снять ещё страницу' : 'Снять сертификат'}>
            <span className="pad" />
          </button>
          {pages.length > 0 ? (
            <button className="btn btn-primary btn-sm" style={{ width: 62, padding: '10px 0' }}
                    onClick={() => void read()}>
              Готово
            </button>
          ) : <span style={{ width: 62 }} />}
        </div>

        <div className="status">
          {pages.length
            ? 'Снимите оборот и вкладыши — потом «Готово»'
            : 'Номер отчёта должен быть читаем. Страниц можно несколько'}
        </div>
      </div>
    );
  }

  if (phase === 'reading') {
    return (
      <div className="app">
        <div className="bar">
          <h1 className="t">Читаю отчёт</h1>
          <p className="meta">Несколько секунд.</p>
        </div>
        <div className="scroll">
          {pages[0] && <div className="plate"><img src={pages[0].url} alt="сертификат" /></div>}
          <div style={{ marginTop: 16, display: 'grid', gap: 10 }}>
            <div className="skeleton" style={{ height: 46 }} />
            <div className="skeleton" style={{ height: 46, width: '78%' }} />
            <div className="skeleton" style={{ height: 46, width: '64%' }} />
          </div>
        </div>
      </div>
    );
  }

  // ── что получилось ────────────────────────────────────────────────────────
  //
  // Отдельный экран, а не молчаливый возврат: человек должен видеть, что
  // именно легло в карточку и что с этим будет дальше.
  if (phase === 'done') {
    const read = Object.keys(cert).length > 0;
    return (
      <div className="app">
        <div className="bar">
          <h1 className="t">Отчёт добавлен</h1>
          <p className="meta">
            {pages.length} {pages.length === 1 ? 'страница' : pages.length < 5 ? 'страницы' : 'страниц'}
            {' · камень '}{stone.publicNo ?? stone.localNo}
          </p>
        </div>
        <div className="scroll">
          <div className="attrs" style={{ marginTop: 4 }}>
            <div className="attr">
              <span className="k">Лаборатория</span>
              <span className="v">{lab?.name ?? cert.lab_name ?? cert.lab ?? 'не определена'}</span>
            </div>
            {cert.report_no && (
              <div className="attr">
                <span className="k">Номер отчёта</span>
                <span className="v num">{cert.report_no}</span>
              </div>
            )}
          </div>

          <div className="memo">
            {read ? (
              <>
                <b>Данные отчёта в карточке камня</b> — помечены как заявленные.
                Отметить «сертифицировано» можно там же, в разделе «Сертификат»,
                после сверки с лабораторией.
              </>
            ) : (
              <>
                <b>Снимки сохранены.</b> Прочитать отчёт без связи нельзя — сервер
                разберёт его сам, как только снимки уедут. Переснимать не нужно.
              </>
            )}
          </div>
        </div>
        <div className="dock">
          <button className="btn btn-primary" onClick={onDone}>Вернуться к камню</button>
        </div>
      </div>
    );
  }

  // ── проверка прочитанного ─────────────────────────────────────────────────
  const doubtful = new Set(cert.uncertain ?? []);
  const labName = lab?.name ?? cert.lab_name ?? cert.lab;
  const filled = FIELDS.filter(([k]) => cert[k]);

  return (
    <div className="app">
      <div className="bar">
        <button className="crumb" onClick={onCancel}>‹ Отмена</button>
        <h1 className="t">Что написано в отчёте</h1>
        <p className="meta">Исправлять здесь нельзя — это документ. Ошибку исправит повторный снимок.</p>
      </div>

      <div className="scroll">
        {error && <div className="memo" style={{ marginTop: 0 }}>{error}</div>}
        {dup && (
          <div className="memo warn" style={{ marginTop: 0 }}>
            <b>Этот отчёт уже снят.</b> Номер {cert.report_no} записан за камнем{' '}
            <b>{dup.no ?? '—'}</b>{dup.supplier ? ` (${dup.supplier})` : ''}. Скорее всего,
            это тот же самый камень. Сохранить можно — но в каталоге он будет вторым.
          </div>
        )}
        {pages.length > 0 && (
          <div className="filmstrip" style={{ marginBottom: 14 }}>
            {pages.map((p, i) => <img key={p.url} src={p.url} alt={`страница ${i + 1}`} />)}
          </div>
        )}

        <div className="attrs" style={{ marginTop: 0 }}>
          <div className="attr">
            <span className="k">Лаборатория</span>
            <span className="v">{labName ?? 'не определена'}</span>
            {doubtful.has('lab') && <span className="src src-low">проверьте</span>}
          </div>
          <div className="attr">
            <span className="k">Номер отчёта</span>
            <span className="v num">{cert.report_no ?? '—'}</span>
            {doubtful.has('report_no') && <span className="src src-low">проверьте</span>}
          </div>
          {cert.report_date && (
            <div className="attr">
              <span className="k">Дата</span>
              <span className="v num">{cert.report_date}</span>
            </div>
          )}
        </div>

        {filled.length > 0 && (
          <>
            <div className="head"><h3>Данные отчёта</h3><span>{filled.length}</span></div>
            <div className="attrs" style={{ marginTop: 0 }}>
              {filled.map(([k, label]) => (
                <div className="attr" key={k}>
                  <span className="k">{label}</span>
                  <span className="v">{String(cert[k])}</span>
                  {doubtful.has(k) && <span className="src src-low">проверьте</span>}
                </div>
              ))}
            </div>
          </>
        )}

        {filled.length === 0 && !cert.report_no && !error && (
          <div className="blankslate">
            <b>Ничего не прочиталось</b>
            Снимите ещё раз: отчёт целиком в кадре, без бликов, номер читаем.
          </div>
        )}

        {!Object.keys(cert).length ? (
          <div className="memo">
            <b>Отчёт разберётся позже.</b> Снимок сохранён на устройстве. Как только
            появится связь, он уедет на сервер и данные отчёта сами появятся в карточке
            камня — переснимать не нужно.
          </div>
        ) : (
        <div className="memo">
          <b>Пока это только прочитанная бумага.</b> В карточке эти данные будут
          помечены как заявленные. «Сертифицировано» появится, когда вы
          проверите отчёт у лаборатории — кнопка будет в карточке камня.
          {lab && <><br /><br />{HOW_TO_VERIFY[lab.verify_method] ?? ''}</>}
          {cert.has_qr && <><br />На отчёте есть QR-код — это самый быстрый путь.</>}
        </div>
        )}
      </div>

      <div className="dock">
        <button className="btn btn-secondary" onClick={() => {
          pages.forEach((p) => URL.revokeObjectURL(p.url));
          setPages([]); setCert({}); setLab(null); setDup(null);
          setPhase('camera'); setError('');
        }}>
          Переснять
        </button>
        <button className="btn btn-primary" disabled={saving} onClick={() => void save()}>
          {saving ? 'Сохраняю…' : 'Добавить к камню'}
        </button>
      </div>
    </div>
  );
}
